import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Sparkles, Loader2, CheckCircle, X } from 'lucide-react';
import { aiImagesAPI, getErrorMessage } from '../../services/api';
import type { UrlMedia } from './types';

interface Props {
  setUrlMediaItems: React.Dispatch<React.SetStateAction<UrlMedia[]>>;
}

export const AIGenerateTab: React.FC<Props> = ({ setUrlMediaItems }) => {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState('');
  const [style, setStyle] = useState('auto');
  const [aspectRatio, setAspectRatio] = useState('1:1');
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);

  const QUICK_STYLES = [
    { id: 'auto', label: t('compose_style_auto', 'Auto') },
    { id: 'photo', label: t('compose_style_photo', 'Photo') },
    { id: 'illustration', label: t('compose_style_illustration', 'Illustration') },
    { id: 'flat', label: t('compose_style_flat', 'Flat') },
    { id: 'minimalist', label: t('compose_style_minimal', 'Minimal') },
  ];

  const ASPECT_RATIOS = [
    { id: '1:1', label: '1:1', desc: t('compose_aspect_square', 'Square') },
    { id: '4:5', label: '4:5', desc: t('compose_aspect_portrait', 'Portrait') },
    { id: '16:9', label: '16:9', desc: t('compose_aspect_landscape', 'Landscape') },
    { id: '9:16', label: '9:16', desc: t('compose_aspect_story', 'Story') },
  ];

  const QUICK_PROMPTS = [
    { label: t('compose_prompt_product', 'Product shot'), prompt: "A premium product displayed on a clean white marble surface with soft shadows and natural lighting, professional e-commerce photography" },
    { label: t('compose_prompt_workspace', 'Workspace'), prompt: "A cozy desk with a laptop, coffee cup, and notebook, warm morning light streaming through a window, productivity aesthetic" },
    { label: t('compose_prompt_abstract', 'Abstract BG'), prompt: "A vibrant abstract gradient background with smooth flowing shapes in purple and blue tones, modern and eye-catching for social media" },
    { label: t('compose_prompt_nature', 'Nature'), prompt: "Lush green landscape at golden hour with warm sun rays filtering through trees, calming and serene mood, high quality" },
    { label: t('compose_prompt_quote', 'Quote card'), prompt: "A clean minimal background with soft pastel gradient, subtle texture, perfect as a backdrop for text overlay or motivational quotes" },
    { label: t('compose_prompt_team', 'Team vibe'), prompt: "A diverse team collaborating around a table in a bright modern office, candid and authentic feel, warm natural lighting" },
  ];

  const generate = useMutation({
    mutationFn: () =>
      aiImagesAPI.generate({
        prompt,
        style,
        aspect_ratio: aspectRatio,
        save_to_library: true,
      }),
    onSuccess: (res) => {
      const data = res.data;
      setGeneratedUrl(data.image_url);

      // Add to the URL media list so it's attached to the post
      const aiMedia: UrlMedia = {
        id: data.id || `ai-${Date.now()}`,
        url: data.image_url,
        filename: 'ai-generated-image.png',
        contentType: 'image/png',
        type: 'image',
        status: 'done' as const,
        progress: 100,
        mediaId: data.media_id || undefined,
        altText: '',
      };
      setUrlMediaItems([aiMedia]);
      toast.success(t('compose_image_generated', 'Image generated and attached!'));
    },
    onError: (e: unknown) => {
      toast.error(getErrorMessage(e, t('compose_image_generation_failed', 'Image generation failed')));
    },
  });

  return (
    <div className="space-y-3">
      <textarea
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        placeholder={t('compose_describe_image', 'Describe the image you want to generate...')}
        className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400 transition-colors"
        rows={3}
      />
      {/* Quick prompt suggestions — always visible so user can switch */}
      <div className="flex flex-wrap gap-1.5">
        {QUICK_PROMPTS.map((p) => {
          const isActive = prompt === p.prompt;
          return (
            <button
              key={p.label}
              onClick={() => setPrompt(p.prompt)}
              className={`px-2 py-1 rounded-md text-[10px] font-medium transition-colors border ${
                isActive
                  ? 'bg-purple-100 text-purple-700 border-purple-300 ring-1 ring-purple-200'
                  : 'bg-purple-50 text-purple-600 border-purple-100 hover:bg-purple-100'
              }`}
            >
              {p.label}
            </button>
          );
        })}
      </div>

      {/* Style + Size row */}
      <div className="flex items-center gap-3">
        <div className="flex gap-1.5 flex-1">
          {QUICK_STYLES.map((s) => (
            <button
              key={s.id}
              onClick={() => setStyle(s.id)}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${
                style === s.id
                  ? 'bg-purple-100 text-purple-700 border border-purple-200'
                  : 'bg-gray-50 text-gray-500 border border-gray-100 hover:bg-gray-100'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
        <div className="flex gap-1">
          {ASPECT_RATIOS.map((ar) => (
            <button
              key={ar.id}
              onClick={() => setAspectRatio(ar.id)}
              title={ar.desc}
              className={`px-2 py-1 rounded-md text-[10px] font-semibold transition-all ${
                aspectRatio === ar.id
                  ? 'bg-purple-100 text-purple-700 border border-purple-200'
                  : 'bg-gray-50 text-gray-400 border border-gray-100 hover:text-gray-600'
              }`}
            >
              {ar.label}
            </button>
          ))}
        </div>
      </div>

      <button
        onClick={() => generate.mutate()}
        disabled={!prompt.trim() || generate.isPending}
        className="w-full py-2.5 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold text-xs hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
      >
        {generate.isPending ? (
          <>
            <Loader2 size={14} className="animate-spin" />
            {t('compose_generating', 'Generating...')}
          </>
        ) : (
          <>
            <Sparkles size={14} />
            {t('compose_generate_image', 'Generate Image')}
          </>
        )}
      </button>

      {/* Preview of generated image */}
      {generatedUrl && (
        <div className="relative rounded-lg overflow-hidden border border-gray-100 bg-gray-50 flex items-center justify-center">
          <img src={generatedUrl} alt="AI generated" className="max-h-48 w-auto rounded-lg object-contain" />
          <div className="absolute top-2 right-2 flex items-center gap-1.5">
            <span className="bg-emerald-500 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1">
              <CheckCircle size={10} />
              {t('compose_attached', 'Attached')}
            </span>
            <button
              onClick={() => setGeneratedUrl(null)}
              className="bg-black/50 hover:bg-black/70 text-white rounded-full p-0.5 transition-colors"
            >
              <X size={12} />
            </button>
          </div>
        </div>
      )}

      {!generatedUrl && !generate.isPending && (
        <div className="bg-purple-50 rounded-lg p-3 text-center">
          <p className="text-xs text-purple-600">
            {t('compose_ai_image_help', 'Describe what you need and AI will generate a custom image for your post')}
          </p>
        </div>
      )}
    </div>
  );
};
